/* Slideshow section
 * Authored as a custom element (Web Component).
 * - Cross-fades slides with optional autoplay, arrows, dots and a play/pause control.
 * - Honours prefers-reduced-motion (no autoplay, no Ken Burns).
 * - Lazy media: every slide except the first keeps its real image in data-src /
 *   data-srcset and only swaps it into the <img> on the first user interaction
 *   (or on demand when a slide is navigated to, so autoplay never shows a gap).
 */
if (!customElements.get('slideshow-component')) {
  class SlideshowComponent extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('.slideshow__track');
      this.slides = Array.from(this.querySelectorAll('.slideshow__slide'));
      this.dots = Array.from(this.querySelectorAll('.slideshow__dot'));
      this.prevButton = this.querySelector('.slideshow__arrow--prev');
      this.nextButton = this.querySelector('.slideshow__arrow--next');
      this.toggleButton = this.querySelector('.slideshow__toggle');

      this.total = this.slides.length;
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.timer = null;
      this.mediaLoaded = false;
      this.touchStartX = 0;
      this.pausedByEditor = false;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayDelay = parseInt(this.getAttribute('data-autoplay-speed'), 10) || 5000;
      this.autoplayActive =
        this.getAttribute('data-autoplay') === 'true' && this.total > 1 && !this.reduceMotion;

      this.interactionEvents = ['pointerdown', 'touchstart', 'keydown', 'mousemove', 'wheel', 'scroll'];

      // Bind handlers that are attached to document/window or to child buttons,
      // where `this` would otherwise not be the component.
      this.next = this.next.bind(this);
      this.prev = this.prev.bind(this);
      this.startTimer = this.startTimer.bind(this);
      this.clearTimer = this.clearTimer.bind(this);
      this.handleToggle = this.handleToggle.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleFirstInteraction = this.handleFirstInteraction.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
    }

    connectedCallback() {
      if (this.total === 0) return;

      // The active slide already carries a real src in markup; guard anyway.
      this.loadSlide(this.currentIndex);
      this.setFocusable(this.slides[this.currentIndex], true);

      if (this.total > 1) {
        this.bindEvents();
        this.armLazyMedia();
        this.updateToggle();
        if (this.autoplayActive) this.startTimer();
      }
    }

    disconnectedCallback() {
      this.clearTimer();
      this.unbindEvents();
    }

    /* ---- Events ---- */
    bindEvents() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.prev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.next);
      if (this.toggleButton) this.toggleButton.addEventListener('click', this.handleToggle);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index, true));
      });

      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('mouseenter', this.clearTimer);
      this.addEventListener('mouseleave', this.startTimer);
      this.addEventListener('focusin', this.clearTimer);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });

      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    unbindEvents() {
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      this.interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, this.handleFirstInteraction)
      );
    }

    /* ---- Lazy media ---- */
    armLazyMedia() {
      this.interactionEvents.forEach((evt) =>
        window.addEventListener(evt, this.handleFirstInteraction, { once: true, passive: true })
      );
    }

    handleFirstInteraction() {
      if (this.mediaLoaded) return;
      this.slides.forEach((_, index) => this.loadSlide(index));
      this.mediaLoaded = true;
      this.interactionEvents.forEach((evt) =>
        window.removeEventListener(evt, this.handleFirstInteraction)
      );
    }

    loadSlide(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const img = slide.querySelector('img[data-src]');
      if (!img) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-src');
    }

    /* ---- Navigation ---- */
    goToSlide(index, isManual) {
      if (this.total <= 1) return;

      let next = index;
      if (next < 0) next = this.total - 1;
      if (next >= this.total) next = 0;

      if (next === this.currentIndex) {
        if (isManual) this.restartTimer();
        return;
      }

      // Make sure the target slide (and the one after it) have their media,
      // so autoplay never advances onto an unloaded image.
      this.loadSlide(next);
      this.loadSlide((next + 1) % this.total);

      const current = this.slides[this.currentIndex];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      this.setFocusable(current, false);
      if (this.dots[this.currentIndex]) {
        this.dots[this.currentIndex].setAttribute('aria-current', 'false');
      }

      this.currentIndex = next;

      const active = this.slides[next];
      active.classList.add('is-active');
      active.removeAttribute('aria-hidden');
      this.setFocusable(active, true);
      if (this.dots[next]) {
        this.dots[next].setAttribute('aria-current', 'true');
      }

      if (isManual) this.restartTimer();
    }

    next(event) {
      this.goToSlide(this.currentIndex + 1, !!event);
    }

    prev(event) {
      this.goToSlide(this.currentIndex - 1, !!event);
    }

    setFocusable(slide, focusable) {
      if (!slide) return;
      slide.querySelectorAll('a[href], button').forEach((el) => {
        if (focusable) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    }

    /* ---- Autoplay ---- */
    startTimer() {
      if (!this.autoplayActive || this.pausedByEditor || this.total <= 1) return;
      this.clearTimer();
      this.timer = setInterval(this.next, this.autoplayDelay);
    }

    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartTimer() {
      if (!this.autoplayActive) return;
      this.clearTimer();
      this.startTimer();
    }

    handleToggle() {
      this.autoplayActive = !this.autoplayActive;
      if (this.autoplayActive) this.startTimer();
      else this.clearTimer();
      this.updateToggle();
    }

    updateToggle() {
      if (!this.toggleButton) return;
      const playing = this.autoplayActive;
      this.toggleButton.classList.toggle('is-paused', !playing);
      this.toggleButton.setAttribute('aria-pressed', String(playing));
      this.toggleButton.setAttribute(
        'aria-label',
        playing
          ? this.toggleButton.dataset.pauseLabel || 'Pause slideshow'
          : this.toggleButton.dataset.playLabel || 'Play slideshow'
      );
      if (this.track) this.track.setAttribute('aria-live', playing ? 'off' : 'polite');
    }

    /* ---- Handlers ---- */
    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev(event);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next(event);
      }
    }

    handleFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.startTimer();
    }

    handleVisibility() {
      if (document.hidden) this.clearTimer();
      else this.startTimer();
    }

    onTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
      this.clearTimer();
    }

    onTouchEnd(event) {
      const diff = this.touchStartX - event.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) this.next(event);
        else this.prev(event);
      } else {
        this.startTimer();
      }
    }

    /* ---- Theme editor ---- */
    handleBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.pausedByEditor = true;
      this.clearTimer();
      this.goToSlide(index, false);
    }

    handleBlockDeselect(event) {
      if (this.slides.indexOf(event.target) === -1) return;
      this.pausedByEditor = false;
      this.startTimer();
    }
  }

  customElements.define('slideshow-component', SlideshowComponent);
}
